import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { productUrl, extractIdFromSlug } from '../lib/productUrl';
import { useTranslation } from 'react-i18next';
import { productsApi } from '../lib/api';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useTrackProductView } from '../lib/tracking';
import { isSaleSku } from '../lib/saleItems';
import ProductCard from '../components/ProductCard';
import ProductReviews from '../components/ProductReviews';
import DeliveryProductConfigurator from '../components/DeliveryProductConfigurator';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import FastGripLogo from '../components/FastGripLogo';
import { Plus, Minus, ShoppingCart, Check, ChevronLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';

const ProductTabs = ({ kitContent, descHtml, navPath = [] }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(kitContent ? 'included' : 'description');

  // Categories where the "Inclus dans la location" tab should NOT be shown —
  // the vehicle rental products (12M3 / 20M3) have their inclusions listed
  // directly in the main description, not in a separate kit-content block.
  const HIDE_INCLUDED_CATEGORIES = new Set(['12m3', '20m3']);
  const hideIncluded = navPath.some((seg) =>
    seg && HIDE_INCLUDED_CATEGORIES.has(String(seg).trim().toLowerCase())
  );

  // Hide lines that start with "***" — internal back-office annotations
  // (e.g. "***INNPORT"). Applied to both kit-content (plain text) and the
  // rich description (HTML, walked via DOMParser).
  const cleanKitContent = (text) => {
    if (!text) return '';
    return text.split(/\r?\n/).filter((l) => !l.trimStart().startsWith('***')).join('\n');
  };

  const cleanDescHtml = (html) => {
    if (!html || typeof window === 'undefined') return html;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div id="root">${html}</div>`, 'text/html');
      const root = doc.getElementById('root');
      // Block-level elements that may carry a "***..." annotation as their whole text.
      const blocks = root.querySelectorAll('p, li, div, h1, h2, h3, h4, h5, h6, td, span');
      blocks.forEach((el) => {
        const txt = (el.textContent || '').trim();
        if (txt.startsWith('***')) el.remove();
      });
      // Also clean stray text nodes (between <br>) that start with ***
      const TEXT_NODE = 3;
      const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      const toClear = [];
      while (walker.nextNode()) {
        const n = walker.currentNode;
        if ((n.nodeValue || '').trim().startsWith('***')) toClear.push(n);
      }
      toClear.forEach((n) => { n.nodeValue = ''; });
      return root.innerHTML;
    } catch {
      return html;
    }
  };

  const cleanedKit = cleanKitContent(kitContent);
  const cleanedDesc = cleanDescHtml(descHtml);

  const tabs = [];
  if (cleanedKit && !hideIncluded) tabs.push({ key: 'included', label: 'Inclus dans la location' });
  if (cleanedDesc) tabs.push({ key: 'description', label: t('product.full_description') });
  if (tabs.length === 0) return null;

  // Fallback: if 'included' was hidden but was the active tab, switch to
  // 'description' so we never render a blank body.
  if (hideIncluded && activeTab === 'included' && tabs.length > 0) {
    setTimeout(() => setActiveTab('description'), 0);
  }

  // Parse kit content: first line = product name, rest = items
  const kitLines = cleanedKit.split(/\r?\n/).filter((l) => l.trim());
  const kitTitle = kitLines.length > 0 ? kitLines[0] : '';
  const kitItems = kitLines.slice(1);

  return (
    <div className="mt-12" data-testid="product-tabs">
      <Separator className="mb-0" />
      <div className="flex border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-6 py-3 text-sm font-semibold transition-colors relative ${
              activeTab === tab.key
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`tab-${tab.key}`}
          >
            {tab.label}
            {activeTab === tab.key && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
        ))}
      </div>
      <div className="py-6">
        {activeTab === 'included' && cleanedKit && (
          <div data-testid="tab-included-content">
            {kitTitle && <p className="font-semibold text-base mb-3">{kitTitle}</p>}
            {kitItems.length > 0 && (
              <ul className="space-y-1.5">
                {kitItems.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <span className="text-primary mt-0.5">&#10003;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {activeTab === 'description' && cleanedDesc && (
          <div className="prose prose-sm max-w-none text-muted-foreground" data-testid="tab-description-content"
            dangerouslySetInnerHTML={{ __html: cleanedDesc }} />
        )}
      </div>
    </div>
  );
};

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const getViewedCount = () => parseInt(sessionStorage.getItem('pzv_viewed_count') || '0', 10);
const incrementViewedCount = () => {
  const count = getViewedCount() + 1;
  sessionStorage.setItem('pzv_viewed_count', String(count));
  return count;
};

const ProductDetailPage = () => {
  const { id: rawId, slug, productSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Accept legacy /produit/{id}, canonical /location-{slug}-{id} (via
  // ProductBySlug adapter with `productSlug` param), and any other slug
  // route that would surface a `slug` param.
  const id = rawId || extractIdFromSlug(productSlug || slug);
  const { t, i18n } = useTranslation();
  const { addItem, cart, updateQuantity, removeItem, franchiseChoice, setFranchiseChoice } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [crossSells, setCrossSells] = useState([]);
  const [crossSellQty, setCrossSellQty] = useState({});
  // Local "quantity to add" — independent of what's already in the cart.
  // Resets to 1 after every successful "Ajouter au panier" click.
  const [addQuantity, setAddQuantity] = useState(1);
  const [chauffeurProduct, setChauffeurProduct] = useState(null);
  const [chauffeurAdded, setChauffeurAdded] = useState(false);
  const [franchiseDialogOpen, setFranchiseDialogOpen] = useState(false);

  // A truck rental (cam20 / cam12) can offer a 1-click "add chauffeur" button.
  const productSku = String(product?.sku || '').toLowerCase();
  const isTruckRental = productSku.startsWith('cam20') || productSku.startsWith('cam12');
  const chauffeurInCart = cart.items.some(it => String(it.product_sku || '').toLowerCase() === 'camcond');

  useEffect(() => {
    if (!isTruckRental) { setChauffeurProduct(null); return; }
    let alive = true;
    productsApi.getProductBySku('camcond', i18n.language)
      .then(res => { if (alive) setChauffeurProduct(res.data); })
      .catch(() => { if (alive) setChauffeurProduct(null); });
    return () => { alive = false; };
  }, [isTruckRental, i18n.language]);

  const handleAddChauffeur = () => {
    if (!chauffeurProduct) return;
    addItem(chauffeurProduct, 1);
    setChauffeurAdded(true);
    setTimeout(() => setChauffeurAdded(false), 2500);
  };

  const cartItem = cart.items.find(item => item.product_id === id);
  const quantityInCart = cartItem?.quantity || 0;

  useTrackProductView(id);

  useEffect(() => {
    window.scrollTo(0, 0);
    incrementViewedCount();
    setSelectedImageIndex(0);
  }, [id]);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await productsApi.getProduct(id, i18n.language);
        setProduct(response.data);
        // Canonicalise the URL: any legacy /produit/{id} (or slug variant that
        // doesn't match the current name) is redirected to the SEO URL.
        const canonical = productUrl(response.data);
        if (location.pathname !== canonical) {
          navigate(canonical + location.search + location.hash, { replace: true });
        }
      } catch (error) {
        console.error('Failed to fetch product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, i18n.language, location.pathname, location.search, location.hash, navigate]);

  // Fetch cross-sell options
  useEffect(() => {
    const fetchCrossSells = async () => {
      try {
        const res = await fetch(`${API_BASE}/product-options/${id}`);
        const data = await res.json();
        setCrossSells(data.options || []);
        setCrossSellQty({});
      } catch (e) { setCrossSells([]); }
    };
    fetchCrossSells();
  }, [id]);

  useEffect(() => {
    if (!product) return;
    const fetchRecs = async () => {
      try {
        const cartIds = cart.items.map(i => i.product_id).join(',');
        const params = new URLSearchParams({
          viewed_count: String(getViewedCount()),
          ...(cartIds && { cart_ids: cartIds }),
          ...(user?.email && { user_email: user.email })
        });
        const res = await fetch(`${API_BASE}/products/${id}/recommendations?${params}`);
        const data = await res.json();
        setRecommendations(data.recommendations || []);
      } catch (e) {
        console.error('Recommendations error:', e);
      }
    };
    fetchRecs();
  }, [product, id, user, cart.items]);

  // Cross-sell quantity helpers
  const setCsQty = (name, delta) => {
    setCrossSellQty(prev => {
      const newQty = Math.max(0, (prev[name] || 0) + delta);
      if (newQty === 0) {
        const next = { ...prev };
        delete next[name];
        return next;
      }
      return { ...prev, [name]: newQty };
    });
  };

  // Pricing — cross-sell accessories at full price
  const crossSellTotal = crossSells.reduce((sum, cs) => {
    return sum + (cs.daily_price || 0) * (crossSellQty[cs.name] || 0);
  }, 0);
  const totalDailyPrice = (product?.daily_price || 0) + crossSellTotal;
  const hasCrossSells = crossSells.length > 0;
  const selectedCrossSells = crossSells.filter(cs => (crossSellQty[cs.name] || 0) > 0);

  const handleAddToCart = () => {
    if (!product) return;
    addItem(product, addQuantity);
    // Add each selected cross-sell at full price — pass quantity to avoid
    // stale-closure bugs when adding multiple cross-sells in rapid succession.
    selectedCrossSells.forEach(cs => {
      const qty = crossSellQty[cs.name] || 0;
      if (qty <= 0) return;
      const price = cs.daily_price || 0;
      addItem({
        id: cs.linked_product_id || `${product.id}_cs_${cs.sku || cs.name}`,
        name: cs.name,
        sku: cs.sku || '',
        daily_price: price,
        image_url: cs.image_url || product.image_url,
        is_cross_sell: true,
        parent_product_id: product.id
      }, qty);
    });
    // Reset selection for the next add
    setAddQuantity(1);
    setCrossSellQty({});
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
    // Auto-suggest franchise buyback when a truck is added and no choice yet
    if (isTruckRental && !franchiseChoice) {
      setFranchiseDialogOpen(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 w-32 mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="aspect-square bg-gray-200" />
              <div className="space-y-4">
                <div className="h-8 bg-gray-200 w-1/3" />
                <div className="h-10 bg-gray-200 w-2/3" />
                <div className="h-24 bg-gray-200" />
                <div className="h-12 bg-gray-200 w-1/4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen py-8">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h1 className="text-2xl font-bold mb-4">{t('product.not_found')}</h1>
          <Link to="/catalogue">
            <Button variant="outline" className="rounded-none">
              <ChevronLeft className="w-4 h-4 mr-2" />
              {t('product.back_to_catalogue')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const isDeliveryProduct = ['livr', 'livd'].includes(product.sku?.toLowerCase());

  // Best-effort brand extraction — matches the backend `extract_brand` logic.
  const KNOWN_BRANDS = new Set([
    'sony','canon','arri','red','blackmagic','panasonic','fujifilm','nikon',
    'sigma','zeiss','cooke','leica','angenieux','atomos','aputure','astera',
    'kino','kinoflo','dedolight','dji','ronin','tilta','smallhd','teradek',
    'sennheiser','rode','zoom','sachtler','manfrotto','libec','vinten',
    'cartoni','easyrig','steadicam','movi','freefly','godox','nanlite',
    'quasar','amaran','hive','litepanels','chimera','westcott','profoto',
  ]);
  const brandName = (() => {
    const first = String(product.name || '').trim().split(/\s+/)[0] || '';
    if (KNOWN_BRANDS.has(first.toLowerCase())) return first;
    return product.category_name || 'Puzzle Video';
  })();
  const availability = (() => {
    const raw = String(product.availability_status || '').toLowerCase();
    if (['out_of_stock','unavailable','sold_out'].includes(raw)) return 'https://schema.org/OutOfStock';
    if (['preorder','pre_order'].includes(raw)) return 'https://schema.org/PreOrder';
    return 'https://schema.org/InStock';
  })();

  return (
    <div className="min-h-screen py-8" data-testid="product-detail-page">
      <SEO
        title={product.name}
        description={`${isSaleSku(product.sku) ? 'Vente' : 'Location'} ${product.name} — ${product.daily_price > 0 ? product.daily_price.toFixed(2) + ' € HT' + (isSaleSku(product.sku) ? '' : '/jour') : 'Sur devis'}. ${product.category_name || 'Matériel audiovisuel professionnel'}. Puzzle Video Paris.`}
        image={product.image_url}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": product.name,
          "image": product.images || [product.image_url],
          "description": `Location ${product.name} — matériel audiovisuel professionnel`,
          "sku": product.sku,
          "brand": { "@type": "Brand", "name": brandName },
          "offers": {
            "@type": "Offer",
            "priceCurrency": "EUR",
            "price": product.daily_price > 0 ? product.daily_price.toFixed(2) : undefined,
            "priceValidUntil": new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
            "availability": availability,
            "seller": { "@type": "Organization", "name": "Puzzle Video" }
          }
        }}
      />
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8 flex-wrap" data-testid="breadcrumb">
          <Link to="/" className="hover:text-primary transition-colors">{t('product.home')}</Link>
          <span>/</span>
          <Link to="/catalogue" className="hover:text-primary transition-colors">{t('catalogue.title')}</Link>
          {product.nav_menu && (
            <>
              <span>/</span>
              <Link to={`/catalogue?nav_menu=${encodeURIComponent(product.nav_menu)}`} className="hover:text-primary transition-colors">
                {product.nav_menu}
              </Link>
            </>
          )}
          {product.nav_sous_menu && (
            <>
              <span>/</span>
              <Link to={`/catalogue?nav_menu=${encodeURIComponent(product.nav_menu)}&nav_sous_menu=${encodeURIComponent(product.nav_sous_menu)}`} className="hover:text-primary transition-colors">
                {product.nav_sous_menu}
              </Link>
            </>
          )}
          {product.nav_sous_sous_menu && (
            <>
              <span>/</span>
              <Link to={`/catalogue?nav_menu=${encodeURIComponent(product.nav_menu)}&nav_sous_menu=${encodeURIComponent(product.nav_sous_menu)}&nav_sous_sous_menu=${encodeURIComponent(product.nav_sous_sous_menu)}`} className="hover:text-primary transition-colors">
                {product.nav_sous_sous_menu}
              </Link>
            </>
          )}
          <span>/</span>
          <span className="text-foreground font-medium truncate max-w-[250px]">{product.name}</span>
        </nav>

        {/* Main layout: image + info (cross-sells inline above price) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          {/* ── Image column ── */}
          <div className="space-y-4">
            <div className="aspect-square overflow-hidden">
              <img
                src={(product.images?.length > 0 ? product.images[selectedImageIndex] : product.image_url) || 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=800'}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-image"
              />
            </div>
            {product.images?.length > 1 && (
              <div className="grid grid-cols-5 gap-2">
                {product.images.slice(0, 5).map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImageIndex(i)}
                    className={`aspect-square overflow-hidden border transition-colors ${selectedImageIndex === i ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-primary'}`}
                    data-testid={`thumbnail-${i}`}
                  >
                    <img src={img} alt={`${product.name} ${i+1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Product info column ── */}
          <div>
            {product.requires_contact && (
              <Badge variant="secondary" className="rounded-none text-xs mb-2">
                {t('product.quote_only')}
              </Badge>
            )}

            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4" data-testid="product-name">
              {product.name}
            </h1>

            {/* ── Cross-sell inline (above price) ── */}
            {hasCrossSells && (
              <div className="mb-6 border border-border" data-testid="cross-sell-panel">
                <div className="bg-black text-white px-4 py-2">
                  <h3 className="text-xs font-bold uppercase tracking-wider">Accessoires recommandés</h3>
                </div>
                <div className="divide-y divide-border">
                  {crossSells.map((cs) => {
                    const qty = crossSellQty[cs.name] || 0;
                    return (
                      <div key={cs.name} className="flex items-center gap-3 px-4 py-3" data-testid={`cross-sell-${cs.sku || cs.name}`}>
                        {cs.image_url && (
                          <Link to={cs.linked_product_id ? productUrl({ id: cs.linked_product_id, name: cs.linked_product_name || cs.name }) : '#'} className="flex-shrink-0">
                            <div className="w-12 h-12 overflow-hidden group">
                              <img src={cs.image_url} alt={cs.name}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform" loading="lazy" />
                            </div>
                          </Link>
                        )}
                        <div className="flex-1 min-w-0">
                          <Link to={cs.linked_product_id ? productUrl({ id: cs.linked_product_id, name: cs.linked_product_name || cs.name }) : '#'}
                            className="text-sm font-medium hover:text-primary transition-colors line-clamp-1 block">
                            {cs.name}
                          </Link>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-primary">{cs.daily_price.toFixed(2)} €/j</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {qty > 0 ? (
                            <>
                              <div className="flex items-center border border-border">
                                <button onClick={() => setCsQty(cs.name, -1)}
                                  className="px-2 py-1.5 hover:bg-surface transition-colors" data-testid={`cs-decrease-${cs.sku}`}>
                                  <Minus className="w-3 h-3" />
                                </button>
                                <span className="px-3 text-sm font-semibold">{qty}</span>
                                <button onClick={() => setCsQty(cs.name, 1)}
                                  className="px-2 py-1.5 hover:bg-surface transition-colors" data-testid={`cs-increase-${cs.sku}`}>
                                  <Plus className="w-3 h-3" />
                                </button>
                              </div>
                              <span className="text-xs font-semibold text-primary w-16 text-right">
                                {(cs.daily_price * qty).toFixed(2)} €/j
                              </span>
                            </>
                          ) : (
                            <Button variant="outline" size="sm" className="rounded-none h-8 text-xs"
                              onClick={() => setCsQty(cs.name, 1)} data-testid={`cs-add-${cs.sku}`}>
                              <Plus className="w-3 h-3 mr-1" /> Ajouter
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {product.daily_price > 0 && !isDeliveryProduct && (
              <div className="mb-6">
                <p className="text-3xl font-bold text-primary" data-testid="product-price">
                  {totalDailyPrice.toFixed(2)} €
                  {!isSaleSku(product.sku) && (
                    <span className="text-lg font-normal text-muted-foreground"> {(product.price_unit || t('common.per_day')).replace(/^€\s*/, '')}</span>
                  )}
                </p>
                {crossSellTotal > 0 && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {product.daily_price.toFixed(2)} € produit + {crossSellTotal.toFixed(2)} € accessoires
                  </p>
                )}
              </div>
            )}

            <Separator className="my-6" />

            {isDeliveryProduct ? (
              <DeliveryProductConfigurator product={product} />
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-border" data-testid="add-quantity-widget">
                    <button onClick={() => setAddQuantity(q => Math.max(1, q - 1))}
                      className="p-3 hover:bg-surface transition-colors" data-testid="decrease-quantity">
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="px-6 font-semibold" data-testid="quantity-display">{addQuantity}</span>
                    <button onClick={() => setAddQuantity(q => q + 1)}
                      className="p-3 hover:bg-surface transition-colors" data-testid="increase-quantity">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <Button onClick={handleAddToCart}
                    className="rounded-none bg-primary hover:bg-primary-dark"
                    data-testid="add-to-cart-btn">
                    {added ? (
                      <><Check className="w-5 h-5 mr-2" />{t('product.added')}</>
                    ) : (
                      <><Plus className="w-5 h-5 mr-2" />{t('product.add_to_cart')}</>
                    )}
                  </Button>
                </div>

                <p className="text-sm text-muted-foreground">{t('product.dates_in_cart')}</p>

                {isTruckRental && chauffeurProduct && (
                  <div className="border border-primary/30 bg-primary/5 p-3 flex items-center justify-between gap-3" data-testid="add-chauffeur-block">
                    <div className="text-sm">
                      <div className="font-semibold">Besoin d&apos;un chauffeur ?</div>
                      <div className="text-xs text-muted-foreground">
                        {chauffeurProduct.name} — {Number(chauffeurProduct.daily_price || 0).toFixed(2)} €/jour
                      </div>
                    </div>
                    <Button
                      onClick={handleAddChauffeur}
                      disabled={chauffeurInCart}
                      className="rounded-none bg-black hover:bg-primary text-white shrink-0"
                      data-testid="add-chauffeur-btn"
                    >
                      {chauffeurInCart ? (
                        <><Check className="w-4 h-4 mr-2" />Déjà dans le panier</>
                      ) : chauffeurAdded ? (
                        <><Check className="w-4 h-4 mr-2" />Ajouté</>
                      ) : (
                        <><Plus className="w-4 h-4 mr-2" />Ajouter chauffeur</>
                      )}
                    </Button>
                  </div>
                )}

                {product.is_fastgrip && (
                  <Link to="/kit-grip" data-testid="fastgrip-link">
                    <Button variant="outline" className="rounded-none w-full md:w-auto border-black text-black hover:bg-black hover:text-white transition-colors">
                      Utilisez notre outil <FastGripLogo size="sm" className="mx-1" /> pour composer votre bijoute
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Full-width tabs: Inclus + Description */}
        {(product.full_desc || product.description_html) && (
          <ProductTabs
            kitContent={product.full_desc}
            descHtml={product.description_html}
            navPath={[product.nav_menu, product.nav_sous_menu, product.nav_sous_sous_menu]}
          />
        )}

        {/* Customer reviews */}
        <ProductReviews productId={product.id} />

        {recommendations.length > 0 && (
          <div className="mt-16" data-testid="recommendations-section">
            <Separator className="mb-8" />
            <h2 className="text-xl font-bold mb-6">{t('product.recommendations')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {recommendations.slice(0, 4).map((rec) => (
                <div key={rec.id} className="transform scale-[1.0]">
                  <ProductCard product={rec} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Franchise buyback auto-suggestion (triggered after adding a truck) */}
      <Dialog open={franchiseDialogOpen} onOpenChange={setFranchiseDialogOpen}>
        <DialogContent className="max-w-lg rounded-none" data-testid="franchise-suggest-dialog">
          <DialogHeader>
            <DialogTitle>Souhaitez-vous un rachat de franchise ?</DialogTitle>
            <DialogDescription>
              La franchise standard sur nos véhicules est de 3&nbsp;000&nbsp;€&nbsp;HT en cas de sinistre.
              Vous pouvez la réduire ou la supprimer&nbsp;:
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <button
              type="button"
              onClick={() => { setFranchiseChoice('partial'); setFranchiseDialogOpen(false); }}
              className="w-full text-left border border-border p-3 hover:border-primary hover:bg-primary/5 transition-colors"
              data-testid="suggest-franchise-partial"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold">Rachat partiel</span>
                <span className="text-primary font-semibold">19&nbsp;€/jour + 200&nbsp;€ HT</span>
              </div>
              <p className="text-xs text-muted-foreground">Franchise ramenée à 1&nbsp;000&nbsp;€ HT en cas de sinistre.</p>
            </button>

            <button
              type="button"
              onClick={() => { setFranchiseChoice('total'); setFranchiseDialogOpen(false); }}
              className="w-full text-left border border-border p-3 hover:border-primary hover:bg-primary/5 transition-colors"
              data-testid="suggest-franchise-total"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold">Rachat total</span>
                <span className="text-primary font-semibold">29&nbsp;€/jour + 200&nbsp;€ HT</span>
              </div>
              <p className="text-xs text-muted-foreground">Franchise supprimée (0&nbsp;€ HT en cas de sinistre).</p>
            </button>

            <button
              type="button"
              onClick={() => { setFranchiseChoice(null); setFranchiseDialogOpen(false); }}
              className="w-full text-left border border-dashed border-border p-3 hover:bg-gray-50 transition-colors text-muted-foreground"
              data-testid="suggest-franchise-none"
            >
              Non merci, je conserve la franchise standard (3&nbsp;000&nbsp;€ HT)
            </button>
          </div>

          <DialogFooter className="text-xs text-muted-foreground">
            Vous pourrez modifier ce choix depuis votre panier à tout moment.
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductDetailPage;
